import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import CompanyTable from './CompanyTable';
import { BarChart, BigFigure, Card, Message, Notes, PeriodPills, ReportPage } from './ReportParts';
import { downloadReport, periodBody, postReport } from './reportsApi';
import { bigMoney, countText, moneyExact, supplierName, withoutRateText } from './reportFormat';
import { useReportPeriod } from './periodStore';
import { useReport } from './useReport';

const KINDS = ['this_week', 'this_month', 'this_year', 'custom'];

// Leo's "Report: commissions": period pills right of the title, the total (large) with the chart (by day, week or month, following the period), the "By company" table
// and the statement. Commission = net invoiced of the invoices whose payment term has ended in the period, times the supplier's rate. "Export statement" is a button in
// each company's row: one PDF for that company only, never one file with several suppliers. "Upload statement" (Leo's second card) opens the upload; the statements already
// uploaded are listed under it and reopen with their result.
const dayText = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function UploadCard() {
  const t = useT();
  return (
    <Link to="/reports/commissions/statement/new" data-testid="upload-statement" style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '22px 24px', border: `1px solid ${L.border}`, borderRadius: 16, background: L.card, textDecoration: 'none', color: L.text }}>
      <span style={{ width: 56, height: 56, flexShrink: 0, borderRadius: 14, background: L.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={L.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontFamily: L.serif, fontSize: 22, fontWeight: 500 }}>{t('reports.stmt.card')}</span>
      </span>
    </Link>
  );
}

// the statements already uploaded and compared, newest first; each opens its kept result
function KeptStatements() {
  const t = useT();
  const { status, data, retry } = useReport('statement-list', {});
  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);
  const remove = async (id) => {
    setDeleting(true); setFailed(false);
    try {
      const res = await postReport('statement-delete', { id });
      if (!res.ok) throw new Error('delete');
      setConfirming(null); retry();
    } catch { setFailed(true); }
    setDeleting(false);
  };
  if (status === 'error') return <p role="alert" data-testid="statements-error" style={{ margin: 0, fontSize: 14, color: L.muted }}>{t('reports.stmt.list.error')}</p>;
  if (status !== 'ready' || !data.statements.length) return null;
  return (
    <Card title={t('reports.stmt.list.title')} testid="statements-kept">
      {data.statements.map((s) => (
        <div key={s.id} data-testid={`kept-row-${s.id}`}>
        <Link to={`/reports/commissions/statement/${s.id}`} data-testid={`kept-${s.id}`}
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 16px', padding: '12px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text, textDecoration: 'none' }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <strong style={{ overflowWrap: 'anywhere' }}>{supplierName(s.supplier, t)}</strong>
            <span style={{ fontSize: 13, color: L.muted, overflowWrap: 'anywhere' }}>{s.file.name} · {s.lines.read === 1 ? t('reports.stmt.upload.lines.one', { n: '1' }) : t('reports.stmt.upload.lines.many', { n: countText(s.lines.read) })}</span>
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', fontSize: 14, color: L.muted }}>
            <span>{dayText(s.period.from)} – {dayText(s.period.to)}</span>
            {s.result && s.result.base && <span data-testid={`kept-result-${s.id}`}>{t('reports.stmt.list.result', { agree: countText(s.result.agrees), diff: s.result.difference == null ? '—' : `${s.result.difference > 0 ? '+' : ''}${moneyExact(s.result.difference, s.result.base)}` })}</span>}
          </span>
        </Link>
        {data.can_delete && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingBottom: 10 }}>
            {confirming !== s.id ? (
              <button type="button" onClick={() => { setConfirming(s.id); setFailed(false); }} data-testid={`kept-delete-${s.id}`} style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 13, color: L.muted, cursor: 'pointer', textDecoration: 'underline' }}>{t('reports.stmt.delete')}</button>
            ) : (
              <>
                <span data-testid={`kept-confirm-${s.id}`} style={{ fontSize: 13, color: L.muted }}>{t('reports.stmt.delete.confirm')}</span>
                <button type="button" disabled={deleting} onClick={() => remove(s.id)} data-testid={`kept-delete-yes-${s.id}`} style={{ height: 32, padding: '0 14px', borderRadius: 16, font: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#9E2A1C', color: '#FFFFFF', border: '1px solid #9E2A1C' }}>{t('reports.stmt.delete.yes')}</button>
                <button type="button" disabled={deleting} onClick={() => setConfirming(null)} data-testid={`kept-delete-no-${s.id}`} style={{ height: 32, padding: '0 14px', borderRadius: 16, font: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#FFFFFF', color: L.text, border: `1px solid ${L.border}` }}>{t('unc.cancel')}</button>
                {failed && <span role="alert" data-testid="kept-delete-failed" style={{ fontSize: 13, color: '#9E2A1C' }}>{t('reports.stmt.delete.failed')}</span>}
              </>
            )}
          </div>
        )}
        </div>
      ))}
    </Card>
  );
}

export default function CommissionsView() {
  const t = useT();
  const phone = useViewportKind() === 'phone';
  const [period, setPeriod] = useReportPeriod('past');
  const { status, data, retry } = useReport('commissions', periodBody(period));
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);

  const exportStatement = async (row) => {
    setBusy(row.supplier.id); setFailed(false);
    const ok = await downloadReport('statement', { supplier: row.supplier.id, ...periodBody(period) }, 'commission-statement.pdf');
    setBusy(null);
    if (!ok) setFailed(true);
  };

  return (
    <ReportPage title={t('reports.card.commissions')} backTo="/reports" pills={<PeriodPills kinds={KINDS} value={period} onChange={setPeriod} />} testid="report-commissions">
      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          <Card testid="commissions-top" style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : 'minmax(220px, 1fr) minmax(0, 2fr)', gap: 24, alignItems: 'center' }}>
            <BigFigure label={t('reports.commissions.earned')} value={bigMoney(data.total.commission, data.currency)} color={L.green}
              sub={withoutRateText(t, data.total.without_rate.companies) || null} />
            <BarChart series={data.series} grain={data.period.grain} currency={data.currency} color={L.green} />
          </Card>
          <Card title={t('reports.byCompany')} testid="commissions-companies">
            <CompanyTable rows={data.companies} total={data.total} currency={data.currency} barColor={L.green} netLabel={t('reports.col.netInvoiced')} commissionLabel={t('reports.col.commission')}
              action={{ label: t('reports.statement.export'), busyId: busy, onClick: exportStatement }} />
            {failed && <p role="alert" data-testid="statement-failed" style={{ margin: '12px 0 0', color: L.accent, fontSize: 14 }}>{t('reports.statement.failed')}</p>}
          </Card>
          <UploadCard />
          <KeptStatements />
          <Notes notes={data.notes} extra={[t('reports.note.commissionBasis')]} />
        </>
      )}
    </ReportPage>
  );
}
